/**
 * Who brought a client, and what the friends a client brought earned it
 * (owner, 2026-10-29). Recorded once and never changed: from then on it is
 * money, so the administrator's password is asked again.
 */
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";

import { Button } from "@/design-system/Button";
import { setPlatformClientReferrer } from "@/shared/api";
import type { ClientOperationsResponse, PlatformClientResponse } from "@/shared/api";
import { money } from "@/shared/money/money";
import { plural } from "@/shared/text/plural";

const FIELD =
  "min-h-9 rounded-row border border-border-strong bg-surface-default px-2 text-text-primary focus-visible:outline-2 focus-visible:outline-focus-ring";

export function ClientReferrer({ client, operations, others }: {
  readonly client: PlatformClientResponse;
  readonly operations: ClientOperationsResponse | undefined;
  /** The other clients, any of whom may have brought this one. */
  readonly others: readonly PlatformClientResponse[];
}): React.JSX.Element {
  const queries = useQueryClient();
  const [editing, setEditing] = useState(false);
  const [referrer, setReferrer] = useState("");
  const [password, setPassword] = useState("");
  const save = useMutation({
    mutationFn: async () => {
      const chosen = others.find((other) => `${other.tenant_id}/${other.account_id}` === referrer);
      if (chosen === undefined) throw new Error("refused");
      const { response } = await setPlatformClientReferrer({
        path: { tenant_id: client.tenant_id, account_id: client.account_id },
        body: { referrer_tenant_id: chosen.tenant_id, referrer_account_id: chosen.account_id, current_password: password },
      });
      if (response?.ok !== true) throw new Error(response?.status === 403 ? "password" : "refused");
    },
    onSuccess: async () => {
      setEditing(false);
      await queries.invalidateQueries();
    },
    onSettled: () => { setPassword(""); },
  });

  const by = operations?.referred_by ?? null;
  const name = by === null ? null : others.find((other) => `${other.tenant_id}/${other.account_id}` === by)?.display_name ?? by;
  const friends = operations?.friends ?? 0;
  return (
    <div className="flex flex-col gap-1" data-testid="client-referrer">
      <span className="text-text-primary">
        {name === null ? "никто" : name}
        {name === null && !editing && (
          <Button variant="ghost" size="sm" className="ml-2" onClick={() => { setEditing(true); }}>Указать</Button>
        )}
      </span>
      <span className="text-helper-sm text-text-muted">
        {friends === 0
          ? "сам никого не пригласил"
          : `пригласил ${String(friends)} ${plural(friends, "друга", "друзей", "друзей")} · начислено ${money(operations?.referral_earned ?? "0")} USDT`}
      </span>
      {editing && (
        <form className="flex flex-col gap-2" onSubmit={(event) => { event.preventDefault(); if (!save.isPending) save.mutate(); }}>
          <label className="flex flex-col gap-1">
            <span className="text-helper-sm">Кто пригласил</span>
            <select aria-label="Кто пригласил" className={`${FIELD} cursor-pointer`} required value={referrer} onChange={(event) => { setReferrer(event.target.value); }}>
              <option value="">выберите клиента</option>
              {others.map((other) => (
                <option key={`${other.tenant_id}/${other.account_id}`} value={`${other.tenant_id}/${other.account_id}`}>{other.display_name}</option>
              ))}
            </select>
          </label>
          <label className="flex flex-col gap-1">
            <span className="text-helper-sm">Ваш пароль</span>
            <input aria-label="Ваш пароль" className={`${FIELD} w-40`} type="password" autoComplete="current-password" required value={password} onChange={(event) => { setPassword(event.target.value); }} />
          </label>
          <span className="text-helper-sm">Указывается один раз. Пригласивший получает 5 % от прибыли этого клиента из комиссии платформы — со дня, когда вы это укажете.</span>
          <div className="flex gap-2">
            <Button type="submit" variant="primary" disabled={save.isPending || referrer === "" || password === ""}>Сохранить</Button>
            <Button onClick={() => { setEditing(false); }}>Отмена</Button>
          </div>
          {save.isError && (
            <span role="alert" className="text-helper-sm text-status-warning">
              {save.error.message === "password" ? "Пароль не подошёл." : "Не принято: у клиента уже есть пригласивший."}
            </span>
          )}
        </form>
      )}
    </div>
  );
}
